import { useState, useCallback } from "react";
import { Editor } from "@tiptap/react";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { CropCoordinates } from "@/zapi-frontend/widgets/ImageCropper/types";
import { useEditorStore } from "@/zapi-frontend/stores/editor";

export const useCropManagement = (editor: Editor | null) => {
  const [recropModalOpen, setRecropModalOpen] = useState(false);

  const [recropImage, setRecropImage] = useState<{
    id: number;
    imageUrl: string;
    cropCoordinates: CropCoordinates;
  } | null>(null);

  const { imgsCropUpdated } = useEditorStore();

  const handleImageClick = useCallback(
    (id: number, _: string) => {
      const currentChapter = useChapterStore.getState().activeChapter;
      if (!currentChapter?.id) return;

      let imageData = {
        id,
        imageUrl: "",
        tlCoordinateX: 0,
        tlCoordinateY: 0,
        brCoordinateX: 0,
        brCoordinateY: 0
      };
      const found = currentChapter.activeMemo?.croppedMemoImages?.find((img) => img.id === id);
      if (found) {
        imageData = found;
      } else if (editor) {
        editor.state.doc.descendants((node) => {
          if (node.type.name === "image" && node.attrs.imageId === id) {
            imageData.imageUrl = node.attrs.src;
            return false;
          }
          return true;
        });
      }

      setRecropImage({
        ...imageData,
        cropCoordinates: {
          topLeft: { x: imageData.tlCoordinateX, y: imageData.tlCoordinateY },
          bottomRight: { x: imageData.brCoordinateX, y: imageData.brCoordinateY }
        }
      });
      setRecropModalOpen(true);
    },
    [editor]
  );

  const handleRecropSuccess = useCallback(
    (coordinates: CropCoordinates) => {
      const currentChapter = useChapterStore.getState().activeChapter;
      if (currentChapter?.id && recropImage) {
        useChapterStore
          .getState()
          .updateChapterCroppedMemoImage(currentChapter.id.toString(), {
            id: recropImage.id,
            imageUrl: recropImage.imageUrl,
            tlCoordinateX: coordinates.topLeft.x,
            tlCoordinateY: coordinates.topLeft.y,
            brCoordinateX: coordinates.bottomRight.x,
            brCoordinateY: coordinates.bottomRight.y
          })
          .then(() => {
            imgsCropUpdated?.();
          });
      }

      if (editor && recropImage) {
        const img = new window.Image();
        img.onload = () => {
          const width = img.naturalWidth;
          const height = img.naturalHeight;
          editor.commands.command(({ tr, state }) => {
            state.doc.descendants((node, pos) => {
              if (node.type.name === "image" && node.attrs.src === recropImage.imageUrl) {
                tr.setNodeMarkup(pos, undefined, {
                  ...node.attrs,
                  tlx: coordinates.topLeft.x,
                  tly: coordinates.topLeft.y,
                  brx: coordinates.bottomRight.x,
                  bry: coordinates.bottomRight.y,
                  w: width,
                  h: height
                });
              }
            });
            return true;
          });
        };
        img.src = recropImage.imageUrl;
        img.id = recropImage.id.toString();
      }

      setRecropModalOpen(false);
    },
    [editor, recropImage, imgsCropUpdated]
  );

  const applyCropDataToImages = useCallback(() => {
    if (!editor) return;

    const currentChapter = useChapterStore.getState().activeChapter;
    if (!currentChapter?.activeMemo?.croppedMemoImages) return;

    const croppedImages = [...currentChapter.activeMemo.croppedMemoImages];

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    editor.commands.command(({ state, tr }) => {
      const changes: { pos: number; attrs: Record<string, unknown> }[] = [];
      const imageLoadPromises: Promise<void>[] = [];

      state.doc.descendants((node, pos) => {
        if (node.type.name === "image") {
          const imageUrl = node.attrs.src;
          const cropDataIndex = croppedImages.findIndex((img) => img.imageUrl === imageUrl);

          if (cropDataIndex !== -1) {
            const cropData = croppedImages[cropDataIndex];
            croppedImages.splice(cropDataIndex, 1);

            const imageLoadPromise = new Promise<void>((resolve) => {
              const img = new window.Image();
              img.onload = () => {
                const width = img.naturalWidth;
                const height = img.naturalHeight;
                changes.push({
                  pos,
                  attrs: {
                    ...node.attrs,
                    imageId: cropData.id,
                    tlx: cropData.tlCoordinateX,
                    tly: cropData.tlCoordinateY,
                    brx: cropData.brCoordinateX,
                    bry: cropData.brCoordinateY,
                    w: width,
                    h: height
                  }
                });
                resolve();
              };
              img.onerror = () => resolve();
              img.src = imageUrl;
            });
            imageLoadPromises.push(imageLoadPromise);
          } else {
            if (!node.attrs.imageId || node.attrs.imageId === 0) {
              const uniqueId = Date.now() + Math.floor(Math.random() * 1000000);
              changes.push({
                pos,
                attrs: {
                  ...node.attrs,
                  imageId: uniqueId
                }
              });
            }
          }
        }
      });

      Promise.all(imageLoadPromises).then(() => {
        if (changes.length > 0) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          editor.commands.command(({ tr, state }) => {
            let finalTr = tr;
            for (const { pos, attrs } of changes) {
              finalTr = finalTr.setNodeMarkup(pos, undefined, attrs);
            }
            return true;
          });
        }
      });

      return true;
    });
  }, [editor]);

  return {
    recropModalOpen,
    recropImage,
    handleImageClick,
    handleRecropSuccess,
    applyCropDataToImages,
    setRecropModalOpen
  };
};
